// Weekly calendar for the reservations dashboard.
// A single 7-day time grid on every viewport; narrow screens scroll it
// horizontally inside this component (the page itself never scrolls sideways).
// Locale: day/month strings formatted via date-fns with active i18n language.

import React, { useMemo, useState, useCallback } from 'react';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  startOfWeek, addWeeks, addDays, isSameDay, isToday, format,
} from 'date-fns';
import { zhHK, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getStatusMeta } from './statusMeta';
import { resolveDayWindow, blockGeometry, hourLabels, minutesToHHMM } from './timeGrid';
import type { Reservation, TimeSlot } from '@app-types/models/reservation';
import type { Stylist, StylistAvailability } from '@app-types/models/stylist';
import type { Business } from '@app-types/models/business';

const HOUR_PX = 56;
const GUTTER = 52;

const HIDDEN_STATUSES = new Set(['cancelled', 'approval-timeout']);

const planName = (r: Reservation, lang: string): string =>
  (r?.planId?.[lang === 'zh' ? 'zh_name' : 'name'] as string) || r?.planId?.name || '';

const HATCH = 'repeating-linear-gradient(45deg, #EFEBE7, #EFEBE7 6px, #E6E0D9 6px, #E6E0D9 12px)';
const NOSHOW_HATCH = 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(198,84,76,0.12) 5px, rgba(198,84,76,0.12) 10px)';

const dayIsOff = (day: Date, availability?: StylistAvailability): boolean => {
  if (!availability) return false;
  return (availability.leaveDates || []).includes(format(day, 'yyyy-MM-dd'))
    || (availability.dayoff || []).includes(day.getDay());
};

const getDayBlocks = (day: Date, availability?: StylistAvailability): TimeSlot[] =>
  (availability?.timeSlots || []).filter((s) => s.isOpen === false && isSameDay(new Date(s.start), day));

interface LaidOutEvent { ev: Reservation; col: number; cols: number; }

const layoutDayEvents = (events: Reservation[]): LaidOutEvent[] => {
  const sorted = [...events].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      || new Date(a.end).getTime() - new Date(b.end).getTime(),
  );
  const out: LaidOutEvent[] = [];
  let cluster: Reservation[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const colEnds: number[] = [];
    const placed = cluster.map((ev) => {
      const s = new Date(ev.start).getTime();
      const e = new Date(ev.end).getTime();
      let ci = colEnds.findIndex((end) => end <= s);
      if (ci === -1) { ci = colEnds.length; colEnds.push(e); } else { colEnds[ci] = e; }
      return { ev, col: ci };
    });
    const cols = colEnds.length;
    placed.forEach((p) => out.push({ ev: p.ev, col: p.col, cols }));
    cluster = [];
  };
  sorted.forEach((ev) => {
    const s = new Date(ev.start).getTime();
    if (cluster.length === 0) { cluster.push(ev); clusterEnd = new Date(ev.end).getTime(); }
    else if (s < clusterEnd) { cluster.push(ev); clusterEnd = Math.max(clusterEnd, new Date(ev.end).getTime()); }
    else { flush(); cluster.push(ev); clusterEnd = new Date(ev.end).getTime(); }
  });
  if (cluster.length) flush();
  return out;
};

interface WeekCalendarProps {
  reservations: Reservation[];
  stylist?: Stylist | null;
  business?: Business | null;
  onSelectBooking: (r: Reservation) => void;
  onCreateSlot?: (day: Date, startTime: string) => void;
  onRemoveBlock?: (block: TimeSlot) => void;
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({ reservations, stylist, business, onSelectBooking, onCreateSlot, onRemoveBlock }) => {
  const { t, i18n } = useTranslation();

  const [weekOffset, setWeekOffset] = useState(0);

  // Locale for date-fns: pick Traditional Chinese for /zh|zh-HK, English otherwise.
  const dfLocale: Locale = (i18n.language || 'en')?.startsWith('zh') ? zhHK : enUS;

  // Week (Mon..Sun) based on current weekOffset.
  const days = useMemo(() => {
    const base = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(base, i));
  }, [weekOffset]);

  const { startMin, endMin } = useMemo(
    () => resolveDayWindow(stylist, business),
    [stylist, business]
  );
  const labels = useMemo(() => hourLabels(startMin, endMin), [startMin, endMin]);
  const gridHeight = labels.length * HOUR_PX;

  // Tap on empty column space -> start a booking at that day + (15-min-rounded) time.
  const handleColumnClick = useCallback((day: Date) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateSlot) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const rawMin = startMin + (offsetY / HOUR_PX) * 60;
    const rounded = Math.min(endMin - 15, Math.max(startMin, Math.floor(rawMin / 15) * 15));
    onCreateSlot(day, minutesToHHMM(rounded));
  }, [onCreateSlot, startMin, endMin]);

  const rangeLabel = `${format(days[0], 'd MMM', { locale: dfLocale })} – ${format(days[6], 'd MMM', { locale: dfLocale })}`;

  return (
    <Box sx={{ minWidth: 0, width: '100%' }}>
      {/* toolbar */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1, width: '100%', minWidth: 0 }}>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setWeekOffset((w) => w - 1)} aria-label="Previous week"><ChevronLeftIcon /></IconButton>
        <Typography fontWeight={600} fontSize={14} sx={{ minWidth: 130, textAlign: 'center' }}>{rangeLabel}</Typography>
        <IconButton size="small" onClick={() => setWeekOffset((w) => w + 1)} aria-label="Next week"><ChevronRightIcon /></IconButton>
      </Stack>

      {/* legend */}
      <Stack direction="row" spacing={2} sx={{ px: 2, py: 1, flexWrap: 'wrap', bgcolor: '#FCFAF8', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        {[['confirmed', 'legendConfirmed'], ['pending-for-approval', 'legendPending'], ['completed', 'legendCompleted'], ['no-show', 'legendNoShow']].map(([s, key]) => (
          <Stack key={key} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: getStatusMeta(s).color }} />
            <Typography fontSize={12} color="text.secondary">{t(`reservationsV2.${key}`)}</Typography>
          </Stack>
        ))}
      </Stack>

      {/* grid: horizontal scroll on narrow screens */}
      <Box sx={{ overflowX: 'auto', minWidth: 0 }}>
        <Box sx={{ minWidth: 720 }}>
          {/* day headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `${GUTTER}px repeat(7, minmax(90px, 1fr))` }}>
            <Box />
            {days.map((d) => {
              const today = isToday(d);
              return (
                <Box key={d.toISOString()} sx={{ textAlign: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: today ? '#FCF8F1' : 'transparent' }}>
                  <Typography fontSize={11} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
                    {format(d, 'EEE', { locale: dfLocale })}
                  </Typography>
                  <Typography fontSize={17} fontWeight={700} sx={today ? { bgcolor: 'primary.main', color: '#fff', width: 30, height: 30, lineHeight: '30px', borderRadius: '50%', mx: 'auto', mt: 0.25 } : { mt: 0.25 }}>
                    {format(d, 'd')}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {/* body: gutter + day columns */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `${GUTTER}px repeat(7, minmax(90px, 1fr))`, position: 'relative' }}>
            <Box sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
              {labels.map((label) => (
                <Box key={label} sx={{ height: HOUR_PX, textAlign: 'right', pr: 0.75, pt: 0.25, fontSize: 11, color: 'text.secondary', borderBottom: '1px solid #F1ECE6' }}>
                  {label}
                </Box>
              ))}
            </Box>
            {days.map((day) => {
              const dayEvents = reservations.filter((r) => isSameDay(new Date(r.start), day) && !HIDDEN_STATUSES.has(r.status));
              const laidOut = layoutDayEvents(dayEvents);
              const off = dayIsOff(day, stylist?.availability);
              const blocks = getDayBlocks(day, stylist?.availability);
              const canCreate = onCreateSlot && !off;
              return (
                <Box
                  key={day.toISOString()}
                  onClick={canCreate ? handleColumnClick(day) : undefined}
                  sx={{ position: 'relative', borderRight: '1px solid', borderColor: 'divider', bgcolor: isToday(day) ? '#FCF8F1' : 'transparent', height: gridHeight, cursor: canCreate ? 'copy' : 'default' }}
                >
                  {labels.map((label) => (
                    <Box key={label} sx={{ height: HOUR_PX, borderBottom: '1px solid #E7E0D8' }}>
                      {[0, 1, 2, 3].map((q) => (
                        <Box
                          key={q}
                          sx={{
                            height: HOUR_PX / 4,
                            boxSizing: 'border-box',
                            borderBottom: q === 3 ? 'none' : (q === 1 ? '1px solid #EFE7DD' : '1px dashed #F1ECE6'),
                            bgcolor: q % 2 === 1 ? 'rgba(199,164,110,0.035)' : 'transparent',
                          }}
                        />
                      ))}
                    </Box>
                  ))}
                  {off && (
                    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: HATCH, opacity: 0.7, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 1, pointerEvents: 'none' }}>
                      <Typography fontSize={11} fontWeight={700} sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('reservationsV2.dayOff')}
                      </Typography>
                    </Box>
                  )}
                  {blocks.map((b) => {
                    const { top, height } = blockGeometry(b as { start: string | Date; end: string | Date }, startMin, endMin, HOUR_PX);
                    return (
                      <Box
                        key={b._id}
                        onClick={(e) => { e.stopPropagation(); if (onRemoveBlock) onRemoveBlock(b); }}
                        title={b.title || t('reservationsV2.blocked')}
                        sx={{
                          position: 'absolute', top, height, left: 4, right: 4,
                          backgroundImage: HATCH, border: '1px solid #D8CFC4', borderRadius: 1,
                          px: 0.75, py: 0.5, overflow: 'hidden', cursor: onRemoveBlock ? 'pointer' : 'default',
                          '&:hover': { boxShadow: 1 },
                        }}
                      >
                        <Typography fontSize={11} fontWeight={600} noWrap sx={{ color: 'text.secondary' }}>
                          🔒 {b.title || t('reservationsV2.blocked')}
                        </Typography>
                      </Box>
                    );
                  })}
                  {laidOut.map(({ ev: r, col, cols }) => {
                    const meta = getStatusMeta(r.status);
                    const { top, height } = blockGeometry(r, startMin, endMin, HOUR_PX);
                    const isDone = r.status === 'completed' || r.status === 'auto-completed';
                    const isNoShow = r.status === 'no-show';
                    return (
                      <Box
                        key={r._id}
                        onClick={(e) => { e.stopPropagation(); onSelectBooking(r); }}
                        sx={{
                          position: 'absolute', top, height,
                          left: `calc(4px + (100% - 8px) * ${col} / ${cols})`,
                          width: `calc((100% - 8px) / ${cols} - 2px)`,
                          bgcolor: meta.bg, borderLeft: '3px solid', borderLeftColor: meta.color,
                          borderRadius: 1, px: 0.75, py: 0.5, overflow: 'hidden', cursor: 'pointer',
                          opacity: isDone ? 0.6 : 1,
                          backgroundImage: isNoShow
                            ? NOSHOW_HATCH
                            : 'none',
                          '&:hover': { filter: 'brightness(0.97)', boxShadow: 1, opacity: 1 },
                        }}
                      >
                        <Typography fontSize={12} fontWeight={700} noWrap sx={{ color: meta.color, textDecoration: isNoShow ? 'line-through' : 'none' }}>
                          {isDone ? '✓ ' : ''}{r.customerName}
                        </Typography>
                        <Typography fontSize={11} noWrap sx={{ color: meta.color, textDecoration: isNoShow ? 'line-through' : 'none' }}>{planName(r, i18n.language)}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WeekCalendar;
